import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

export interface CompletionField {
  /** Human-readable label for the missing-fields tooltip. */
  label: string;
  /** True if the field is filled. */
  filled: boolean;
}

interface CompletionIndicatorProps {
  fields: CompletionField[];
  className?: string;
}

const toneFor = (pct: number) => {
  if (pct >= 80) return 'bg-emerald-500';
  if (pct >= 50) return 'bg-amber-500';
  return 'bg-orange-500';
};

/**
 * Compact progress widget that nudges users to enrich an entity after
 * creation. Computes percentage from a list of (label, filled) pairs and lists
 * the still-missing labels in a tooltip.
 */
export const CompletionIndicator: React.FC<CompletionIndicatorProps> = ({ fields, className }) => {
  const { t } = useTranslation();
  const total = fields.length;
  const filled = fields.filter((f) => f.filled).length;
  const pct = total === 0 ? 100 : Math.round((filled / total) * 100);
  const missing = fields.filter((f) => !f.filled).map((f) => f.label);

  const tooltipText =
    missing.length > 0
      ? t('completion.missing', 'Encore à renseigner : {{list}}', { list: missing.join(', ') })
      : t('completion.allFilled', 'Toutes les informations sont renseignées.');

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2',
        className
      )}
      title={tooltipText}
      role="status"
      aria-label={tooltipText}
    >
      <span className="text-xs font-medium text-foreground whitespace-nowrap">
        {t('completion.label', 'Fiche complète à')}{' '}
        <span className="tabular-nums font-semibold">{pct}%</span>
      </span>
      <div className="relative h-1.5 flex-1 min-w-[6rem] rounded-full bg-muted overflow-hidden">
        <div
          className={cn('absolute left-0 top-0 h-full rounded-full transition-all duration-500', toneFor(pct))}
          style={{ width: `${pct}%` }}
        />
      </div>
      {pct < 100 && (
        <span className="text-[11px] text-muted-foreground whitespace-nowrap hidden md:inline">
          {t('completion.nudge', '{{count}} champ(s) à compléter', { count: missing.length })}
        </span>
      )}
    </div>
  );
};

export default CompletionIndicator;
